import { useState, useMemo, useEffect } from "react";
import { Logo } from "@/components/Logo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, WifiOff, Truck, CheckCircle2, ArrowRight, Zap, Server, Menu, X,
  Layers, Bot, FileSignature, Ruler, BookOpen, Send, CreditCard, Landmark, Users,
  HardHat, Building2, Wind, Droplets, Minus, Rocket, Boxes, Navigation, ClipboardList, Fuel,
  Camera, FileText, Plus, Star,
} from "lucide-react";
import { Eyebrow } from "@/components/tac";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1758101755915-462eddc23f57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxlbGVjdHJpY2lhbiUyMHRlY2huaWNpYW4lMjB3aXJpbmclMjBwYW5lbCUyMHRhYmxldCUyMGZpZWxkfGVufDB8fHx8MTc4OTg0MDE3MXww&ixlib=rb-4.1.0&q=85";

const PLANS = [
  { key: "starter_monthly", name: "Starter", price: 99, techs: "Up to 3 techs", pop: false,
    features: ["Dispatch command center", "Offline field PWA", "AI predictive material & tool checklists", "Estimates + e-signatures", "Card & ACH payments"] },
  { key: "fleet_monthly", name: "Fleet", price: 249, techs: "Up to 10 techs", pop: true,
    features: ["Everything in Starter", "Voice debrief AI review", "Multi-truck inventory & auto-restock POs", "Anti-forgetting GPS roll-out tracking", "QuickBooks Online sync", "COI & permit vault"] },
  { key: "enterprise_monthly", name: "Enterprise", price: 499, techs: "Unlimited techs", pop: false,
    features: ["Everything in Fleet", "Multi-role Start Job & crew controls", "Subcontractor controls", "Advanced reconciliation", "Priority support & SLA"] },
];

const FEATURES = [
  { icon: Layers, t: "Multi-trade vertical engine", d: "One-click industry packs for GC, commercial & residential construction, HVAC, electrical, plumbing and specialized subs - each with trade-native terminology, custom fields and checklists." },
  { icon: WifiOff, t: "Offline-first field ops", d: "Capture jobs, photos, signatures and payments with zero bars; everything syncs on reconnect and signed documents win conflicts." },
  { icon: ClipboardList, t: "AI predictive job checklists", d: "Claude predicts the exact parts, materials and tools a job needs and auto-prices them from your pricebook, so job margin updates live as the list builds." },
  { icon: Navigation, t: "Smart crew & fleet dispatch", d: "Auto-assign the right technician, helper and truck by job complexity, with heavy-lift helper-tech detection and multi-truck inventory checks." },
  { icon: Fuel, t: "Live roll-out & margin tracking", d: "Browser-GPS mileage and fuel overhead feed a real-time job-cost margin from the moment a crew rolls out, with anti-forgetting start reminders." },
  { icon: Bot, t: "Voice debrief + receipt/serial OCR", d: "Techs talk or snap a receipt or equipment nameplate; Claude structures parts, hours, costs and serials into a mandatory human-review queue - never silent automation." },
  { icon: Camera, t: "Photo docs & public timelines", d: "Time, GPS and user-stamped photo feed with AI captions, AI daily logs, on-photo annotations and comments with @mentions, plus shareable client timelines - no login required." },
  { icon: FileText, t: "Branded photo reports", d: "One-tap PDF photo reports with your logo, cover image, 1/2/4-per-page layouts and a client sign-off page - ready for clients and insurance." },
  { icon: Ruler, t: "Blueprint takeoffs & clash detection", d: "Pin devices and draw wire/pipe runs on plans for automatic material takeoffs, then catch cross-trade clashes before the crew rolls a truck." },
  { icon: BookOpen, t: "Pricebook & live job costing", d: "Standardized unit pricing autofills quotes and checklists while material, labor and receipt costs roll into a live margin with per-trade floor guardrails." },
  { icon: Send, t: "Branded quotes & e-sign portal", d: "Send professional PDF quotes; customers review, approve and sign from a public portal on any device, with milestone payment schedules." },
  { icon: CreditCard, t: "Payments & deposits", d: "Stripe cards + ACH with flexible percentage or milestone deposits, collected in the field or straight from the client portal." },
  { icon: Landmark, t: "QuickBooks & Xero sync", d: "Accepted quotes push as invoices to both ledgers and paid status flows back automatically - no double entry." },
  { icon: Truck, t: "Fleet, tools & inventory", d: "Multi-truck stock checks with auto-drafted purchase orders, tool check-in/out, van restock manifests, and mileage and maintenance reminders." },
  { icon: ShieldCheck, t: "Safety & compliance locks", d: "OSHA pre-task gate, lone-worker check-in timer and panic SMS (your own Twilio), plus a COI/license vault that locks dispatch when coverage lapses." },
  { icon: Users, t: "Multi-tenant RBAC", d: "Owner, dispatcher, technician and subcontractor scopes keep every crew and outside sub to exactly the work they're authorized to see." },
];

function Nav() {
  const [open, setOpen] = useState(false);
  const links = [["Features", "#features"], ["Compare", "#compare"], ["Pricing", "#pricing"], ["ROI", "#roi"], ["Trades", "#trades"], ["FAQ", "#faq"]];
  return (
    <header className="fixed inset-x-0 top-0 z-50 glass">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
        <Link to="/" data-testid="brand-home-link" className="flex items-center">
          <Logo />
        </Link>
        <nav className="hidden items-center gap-8 md:flex">
          {links.map(([l, h]) => (
            <a key={l} href={h} className="link-underline text-sm text-slate-300 hover:text-white">{l}</a>
          ))}
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <Link to="/login" data-testid="nav-login-link" className="text-sm font-medium text-slate-200 hover:text-white">Sign in</Link>
          <Link to="/login?mode=signup" data-testid="nav-signup-link" className="btn-amber rounded-full px-5 py-2 text-sm">Start free</Link>
        </div>
        <button data-testid="mobile-menu-btn" className="text-slate-200 md:hidden" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-800 px-5 py-4 md:hidden">
          {links.map(([l, h]) => <a key={l} href={h} onClick={() => setOpen(false)} className="block py-2 text-slate-300">{l}</a>)}
          <Link to="/login" className="mt-2 block py-2 text-white">Sign in</Link>
          <Link to="/login?mode=signup" className="btn-amber mt-2 block rounded-full px-5 py-2 text-center text-sm">Start free</Link>
        </div>
      )}
    </header>
  );
}

function RoiCalculator() {
  const [techs, setTechs] = useState(6);
  const [rate, setRate] = useState(120);
  const [adminHrs, setAdminHrs] = useState(8);
  const { dollars, hours } = useMemo(() => {
    const reclaimedPerTech = adminHrs * 0.6; // hrs/week reclaimed
    const weeklyHours = reclaimedPerTech * techs;
    const annualDollars = weeklyHours * rate * 48;
    return { dollars: Math.round(annualDollars), hours: Math.round(weeklyHours * 48) };
  }, [techs, rate, adminHrs]);
  const Slider = ({ label, testid, value, set, min, max, step, fmt }) => (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm text-slate-300">{label}</span>
        <span className="font-mono-tac text-cyan-400">{fmt(value)}</span>
      </div>
      <input data-testid={testid} type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => set(Number(e.target.value))}
        className="w-full accent-amber-500" />
    </div>
  );
  return (
    <div id="roi" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24">
      <Eyebrow>Return on control</Eyebrow>
      <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">See what you reclaim</h2>
      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        <div className="card-tac space-y-7 p-8">
          <Slider label="Field technicians" testid="roi-tech-count-slider" value={techs} set={setTechs} min={1} max={20} step={1} fmt={(v) => `${v}`} />
          <Slider label="Avg billing rate / hour" testid="roi-rate-slider" value={rate} set={setRate} min={75} max={180} step={5} fmt={(v) => `$${v}`} />
          <Slider label="Weekly admin hours lost / tech" testid="roi-admin-slider" value={adminHrs} set={setAdminHrs} min={1} max={20} step={1} fmt={(v) => `${v}h`} />
        </div>
        <div className="card-tac flex flex-col justify-center gap-6 p-8 tac-grid">
          <div>
            <div className="eyebrow text-cyan-400">Estimated annual recovery</div>
            <div data-testid="roi-dollars-output" className="font-display text-5xl font-900 font-black text-amber-400">${dollars.toLocaleString()}</div>
          </div>
          <div>
            <div className="eyebrow text-cyan-400">Hours reclaimed / year</div>
            <div data-testid="roi-hours-output" className="font-display text-3xl font-bold text-white">{hours.toLocaleString()} hrs</div>
          </div>
          <Link to="/login?mode=signup" className="btn-amber inline-flex w-fit items-center gap-2 rounded-full px-6 py-3 text-sm">
            Capture this <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

const DeployCta = ({ testid }) => (
  <Link to="/login?mode=signup" data-testid={testid}
    className="btn-amber inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold">
    <Rocket className="h-4 w-4" /> Deploy in Minutes - $0 Setup
  </Link>
);

const MATRIX = [
  ["$0 implementation / setup fee", true, false, false, false, false, false],
  ["Flat fleet pricing (no per-user tax)", true, false, false, false, false, false],
  ["No % of contract value taken", true, true, true, true, false, true],
  ["Deep commercial multi-truck inventory (to the component)", true, true, false, false, true, false],
  ["Live job-costing & margin before invoice", true, true, false, false, true, false],
  ["AI voice + photo proposals", true, false, false, false, false, false],
  ["Automated job-creation wizard on e-sign", true, false, false, false, false, false],
  ["Smart tech + helper (heavy-asset) assignment", true, false, false, false, false, false],
  ["Live GPS roll-out + ZIP fuel margin protection", true, false, false, false, false, false],
  ["QuickBooks + Xero two-way ledger sync", true, true, true, true, true, true],
];
const VENDORS = ["SiteControl HQ", "ServiceTitan", "Jobber", "Housecall Pro", "JobTread", "Procore", "Service Fusion"];

const TCO = [
  { v: "ServiceTitan", setup: "$5K - $50K+ onboarding", per: "$300 - $500 / tech / mo", note: "Enterprise lock-in, long contracts" },
  { v: "Procore", setup: "$10K+ implementation", per: "% of Annual Construction Volume", note: "Revenue-based pricing scales against you" },
  { v: "Jobber", setup: "Low setup", per: "$29 - $249 / mo tiered by users", note: "Thin commercial inventory & job-costing" },
  { v: "Housecall Pro", setup: "Low setup", per: "$59 - $299+ / mo per seat", note: "Consumer-service focus, per-seat creep" },
  { v: "JobTread", setup: "Onboarding fee", per: "$149+ / mo tiered", note: "Estimating-first, light field execution" },
  { v: "Service Fusion", setup: "Setup + training", per: "$195 - $495 / mo tiered", note: "Dated field UX, add-on fees" },
];

const FLOW = [
  [Bot, "AI Voice / Image Proposals"],
  [ClipboardList, "Automated Job-Creation Wizard"],
  [Boxes, "Multi-Truck Inventory Check (to the ziptie)"],
  [Users, "Smart Tech & Helper Assignment"],
  [Navigation, "Live GPS Roll-Out"],
  [Fuel, "ZIP-Code Fuel Margin Protection"],
  [Landmark, "QuickBooks & Xero Ledger Sync"],
];

function CompareSection() {
  return (
    <section id="compare" className="scroll-mt-24 border-y border-slate-800 bg-[#0d1420]">
      <div className="mx-auto max-w-7xl px-5 py-24" data-testid="compare-section">
        <Eyebrow>How we stack up</Eyebrow>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h2 className="max-w-2xl font-display text-3xl font-bold text-white sm:text-4xl">Everything the giants gate behind $50K setups - for a flat fleet price.</h2>
          <DeployCta testid="compare-cta-top" />
        </div>
        <p className="mt-3 max-w-3xl text-slate-400">SiteControl HQ out-executes the enterprise giants on cost, the mass-market apps on commercial depth, and construction tools on agile field ops - in one platform.</p>

        {/* Operational flow */}
        <div className="mt-12 flex flex-wrap items-center gap-2" data-testid="operational-flow">
          {FLOW.map(([Icon, label], i) => (
            <div key={label} className="flex items-center gap-2">
              <div className="flex items-center gap-2 rounded-full border border-slate-700 bg-[#0b0f17] px-3 py-2">
                <Icon className="h-4 w-4 text-amber-400" /><span className="text-xs font-medium text-slate-200">{label}</span>
              </div>
              {i < FLOW.length - 1 && <ArrowRight className="h-4 w-4 text-slate-600" />}
            </div>
          ))}
        </div>

        {/* Comparison matrix */}
        <div className="mt-12 overflow-x-auto rounded-2xl border border-slate-800" data-testid="compare-matrix">
          <table className="w-full min-w-[820px] border-collapse text-sm">
            <thead>
              <tr className="bg-[#0b0f17]">
                <th className="p-4 text-left font-mono-tac text-[11px] uppercase tracking-widest text-slate-500">Capability</th>
                {VENDORS.map((v, i) => (
                  <th key={v} className={`p-4 text-center font-display text-xs font-semibold ${i === 0 ? "text-amber-400" : "text-slate-400"}`}>{v}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MATRIX.map((row) => (
                <tr key={row[0]} className="border-t border-slate-800">
                  <td className="p-4 text-slate-200">{row[0]}</td>
                  {row.slice(1).map((val, i) => (
                    <td key={i} className={`p-4 text-center ${i === 0 ? "bg-amber-500/5" : ""}`}>
                      {val ? <CheckCircle2 className={`mx-auto h-5 w-5 ${i === 0 ? "text-amber-400" : "text-emerald-400"}`} /> : <Minus className="mx-auto h-4 w-4 text-slate-600" />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Total Cost of Ownership */}
        <h3 className="mt-16 font-display text-2xl font-bold text-white">The real cost of legacy platforms</h3>
        <p className="mt-2 max-w-3xl text-slate-400">Legacy tools bleed contractors through setup fees, revenue cuts and per-user penalties. SiteControl HQ scales architecturally - flat, with zero implementation fees.</p>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="tco-breakdown">
          <div className="rounded-2xl border-2 border-amber-500/50 bg-amber-500/5 p-6">
            <div className="font-display text-lg font-bold text-amber-300">SiteControl HQ</div>
            <div className="mt-3 font-display text-3xl font-black text-white">$0 setup</div>
            <div className="mt-1 text-sm text-slate-300">Flat fleet pricing · no per-tech tax · no % of contract value</div>
            <div className="mt-4"><DeployCta testid="tco-cta" /></div>
          </div>
          {TCO.map((t) => (
            <div key={t.v} className="rounded-2xl border border-slate-800 bg-[#0b0f17] p-6" data-testid={`tco-${t.v.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-display text-lg font-bold text-slate-200">{t.v}</div>
              <div className="mt-3 text-sm"><span className="font-mono-tac text-[10px] uppercase text-slate-500">Setup</span><div className="text-rose-300">{t.setup}</div></div>
              <div className="mt-2 text-sm"><span className="font-mono-tac text-[10px] uppercase text-slate-500">Ongoing</span><div className="text-rose-300">{t.per}</div></div>
              <p className="mt-3 text-xs text-slate-500">{t.note}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col items-center gap-3 rounded-2xl border border-slate-800 bg-[#0b0f17] p-10 text-center">
          <h3 className="font-display text-2xl font-bold text-white">Stop renting software that taxes every tech and every dollar.</h3>
          <p className="max-w-xl text-slate-400">One platform, from AI proposal to pristine QuickBooks &amp; Xero ledger. No setup fees, ever.</p>
          <DeployCta testid="compare-cta-bottom" />
        </div>
      </div>
    </section>
  );
}

function RatingStars({ n, className = "h-5 w-5" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${className} ${i <= Math.round(n) ? "fill-amber-400 text-amber-400" : "text-slate-700"}`} />
      ))}
    </div>
  );
}

function ReviewsSection({ agg, reviews }) {
  if (!agg || !agg.count) return null;
  return (
    <section id="reviews" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24" data-testid="reviews-section">
      <Eyebrow>Reviews</Eyebrow>
      <h2 className="mt-3 max-w-3xl font-display text-3xl font-bold text-white sm:text-4xl">Rated {agg.avg} out of 5 by the crews and customers who use it.</h2>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <RatingStars n={agg.avg} />
        <span className="text-sm text-slate-400">{agg.avg} average from {agg.count} verified {agg.count === 1 ? "review" : "reviews"}</span>
      </div>
      <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {reviews.slice(0, 6).map((r, i) => (
          <figure key={i} className="card-tac p-6" data-testid="review-testimonial">
            <RatingStars n={r.rating} className="h-4 w-4" />
            {r.comment && <blockquote className="mt-3 text-sm leading-relaxed text-slate-300">"{r.comment}"</blockquote>}
            <figcaption className="mt-4 text-sm font-semibold text-white">
              {r.author_name}{r.author_company ? <span className="font-normal text-slate-500"> · {r.author_company}</span> : null}
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

const FAQS = [
  ["What is SiteControlHQ?", "SiteControlHQ is an offline-first field service management and job-costing platform for contractor fleets. It runs dispatch, AI job checklists, estimates and e-signatures, in-field card and ACH payments, and QuickBooks or Xero sync in one system that works with or without a signal."],
  ["How much does SiteControlHQ cost?", "Three monthly plans: Starter at $99/mo for up to 3 techs, Fleet at $249/mo for up to 10 techs, and Enterprise at $499/mo for unlimited techs. Start free and upgrade when you provision your workspace."],
  ["Does SiteControlHQ work offline?", "Yes. The field PWA captures jobs, photos, signatures and payments with zero bars and syncs automatically on reconnect, and signed documents always win sync conflicts."],
  ["Which trades does it support?", "General contracting, commercial and residential construction, HVAC, electrical, plumbing, and specialized sub-trades such as low-voltage, security, fire and life-safety, networking and solar."],
  ["Does it integrate with QuickBooks and Xero?", "Yes. Accepted quotes push as invoices to both QuickBooks Online and Xero, and paid status flows back automatically with no double entry."],
  ["How does SiteControlHQ handle payments?", "It collects card and ACH payments through Stripe, with flexible percentage or milestone deposits taken in the field or from the client portal."],
];

function FaqSection() {
  return (
    <section id="faq" className="mx-auto max-w-4xl scroll-mt-24 px-5 pb-24" data-testid="faq-section">
      <Eyebrow>FAQ</Eyebrow>
      <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">Questions contractors ask before switching.</h2>
      <div className="mt-10 divide-y divide-slate-800 border-y border-slate-800">
        {FAQS.map(([q, a]) => (
          <details key={q} className="group py-5" data-testid="faq-item">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-lg font-semibold text-white">
              {q}
              <Plus className="h-5 w-5 shrink-0 text-amber-400 transition-transform group-open:rotate-45" />
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

export default function Landing() {
  const [reviews, setReviews] = useState([]);
  const [agg, setAgg] = useState(null);
  useEffect(() => {
    api.get("/public/reviews/featured")
      .then(({ data }) => { setAgg(data.aggregate); setReviews(data.reviews || []); })
      .catch(() => {});
  }, []);
  const jsonLd = agg && agg.show_schema ? {
    "@context": "https://schema.org", "@type": "Product", name: "SiteControlHQ",
    description: "Offline-first field service management and job-costing software for contractor fleets.",
    brand: { "@type": "Brand", name: "SiteControlHQ" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: String(agg.avg), reviewCount: String(agg.count), bestRating: "5", worstRating: "1" },
    review: reviews.slice(0, 6).map((r) => ({
      "@type": "Review",
      reviewRating: { "@type": "Rating", ratingValue: String(r.rating), bestRating: "5" },
      author: { "@type": "Person", name: r.author_name || "Customer" },
      reviewBody: r.comment || "",
    })),
  } : undefined;
  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <Seo path="/" jsonLd={jsonLd} />
      <Nav />

      {/* Hero */}
      <section className="relative overflow-hidden pt-28">
        <div className="absolute inset-0">
          <img src={HERO} alt="Electrician inspecting a control panel in the field" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F17] via-[#0B0F17]/90 to-[#0B0F17]/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 py-24 lg:py-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <Eyebrow>Field ops command · Commercial & trade contractors</Eyebrow>
            <h1 className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Field service power. Office-grade control. Anywhere.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
              SiteControl is the offline-first operating system for contractor fleets - dispatch, signed estimates,
              in-field payments, and AI debriefs that always pass a human review.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/login?mode=signup" data-testid="hero-cta-signup" className="btn-amber inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm">
                Start your workspace <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#pricing" className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-7 py-3.5 text-sm text-slate-200 hover:border-slate-500">
                View pricing
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 font-mono-tac text-xs text-slate-500">
              <span>◇ Works fully offline</span>
              <span>◇ QuickBooks Online sync</span>
              <span>◇ Stripe cards + ACH</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24">
        <Eyebrow>The control layer</Eyebrow>
        <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold text-white sm:text-4xl">
          One platform from first bid to reconciled invoice.
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-slate-400">
          Everything the crew touches - dispatch, field capture, estimates, payments, accounting and safety - auditable end to end.
        </p>
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div key={f.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="card-tac p-6 transition-colors">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-amber-500/10 text-amber-400">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-white">{f.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trades */}
      <section id="trades" className="border-y border-slate-800 bg-[#0d1420] scroll-mt-24">
        <div className="mx-auto grid max-w-7xl gap-5 px-5 py-20 md:grid-cols-3">
          {[[HardHat, "General Contracting", "Multi-trade projects, subs, draws and change orders under one roof.", "general_contracting"],
            [Building2, "Commercial Construction", "Ground-up and tenant-improvement builds with RFIs, submittals and multi-crew scheduling.", "commercial_construction"],
            [Wind, "HVAC / Mechanical", "Install, service and PM agreements with equipment and refrigerant tracking.", "hvac"],
            [Zap, "Electrical", "Panel work, load balancing, service upgrades and code compliance.", "electrical"],
            [Droplets, "Plumbing", "Service, repipe and commercial plumbing with fixtures and permits.", "plumbing"],
            [Server, "Specialized Sub-trades", "Low-voltage, security, fire/life-safety, networking and solar specialists.", "specialized_subs"]].map(([Icon, t, d, slug]) => (
            <Link key={t} to={`/industries/${slug}`} data-testid={`trade-link-${slug}`} className="-m-2 flex items-start gap-4 rounded-xl p-2 transition-colors hover:bg-slate-800/40">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-cyan-500/10 text-cyan-400"><Icon className="h-5 w-5" /></div>
              <div>
                <h3 className="font-display text-lg font-semibold text-white">{t}</h3>
                <p className="mt-1 text-sm text-slate-400">{d}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <CompareSection />

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-24">
        <Eyebrow>Pricing</Eyebrow>
        <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">Priced per fleet, not per headache.</h2>
        <p className="mt-3 max-w-xl text-slate-400">Start free, upgrade when you provision your workspace. Cancel anytime.</p>
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {PLANS.map((p) => (
            <div key={p.key} data-testid={`pricing-card-${p.name.toLowerCase()}`}
              className={`card-tac relative flex flex-col p-8 ${p.pop ? "ring-2 ring-amber-500" : ""}`}>
              {p.pop && <div className="absolute -top-3 left-8 rounded-full bg-amber-500 px-3 py-1 font-mono-tac text-[10px] font-bold uppercase tracking-widest text-black">Most popular</div>}
              <h3 className="font-display text-xl font-semibold text-white">{p.name}</h3>
              <p className="mt-1 text-sm text-slate-400">{p.techs}</p>
              <div className="mt-5 flex items-end gap-1">
                <span className="font-display text-4xl font-black text-white">${p.price}</span>
                <span className="mb-1 text-sm text-slate-500">/mo</span>
              </div>
              <ul className="mt-6 flex-1 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/login?mode=signup" data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                className={`mt-8 rounded-full py-3 text-center text-sm font-semibold ${p.pop ? "btn-amber" : "border border-slate-700 text-white hover:border-slate-500"}`}>
                Get started
              </Link>
            </div>
          ))}
        </div>
      </section>

      <RoiCalculator />

      <ReviewsSection agg={agg} reviews={reviews} />

      <FaqSection />

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 pb-24">
        <div className="card-tac tac-grid flex flex-col items-start gap-6 p-10 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-display text-3xl font-bold text-white">Ready to take control?</h2>
            <p className="mt-2 text-slate-400">Spin up a workspace in under a minute. No card to start.</p>
          </div>
          <Link to="/login?mode=signup" className="btn-amber inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm">
            Create workspace <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-[#0d1420]">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-12 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2.5">
            <Logo />
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/refund-policy" className="hover:text-white">Refund Policy</Link>
            <Link to="/eula" className="hover:text-white">EULA</Link>
            <Link to="/security" className="hover:text-white">Security</Link>
            <Link to="/dpa" className="hover:text-white">DPA</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
          <p className="font-mono-tac text-xs text-slate-600">© 2026 SiteControl · US-based</p>
        </div>
      </footer>
    </div>
  );
}
